import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { withRouter } from 'react-router-dom';
import { FormattedMessage, useIntl } from 'react-intl';
import * as installationActions from '../../actions/installation';
import MikaPageHeader from '../../components/MikaPageHeader/MikaPageHeader';
import { toast } from 'react-toastify';
import '../Contact/Contact.scss';
import '../Transport/Transport.scss';

const inputStyle: React.CSSProperties = {
    width: '100%', padding: '8px 12px', borderRadius: 6, border: '1px solid #d0dbe8',
    fontSize: 13, outline: 'none', boxSizing: 'border-box',
};

interface Props {
    actions: any;
    history: any;
    loading: boolean;
}

const InstallationWithoutForm: React.FC<Props> = (props) => {
    const intl = useIntl();
    const fm = (id: string, defaultMessage: string, values?: Record<string, any>) =>
        intl.formatMessage({ id, defaultMessage }, values);

    const [source, setSource] = useState<'transport' | 'warehouse' | null>(null);
    const [itemId, setItemId] = useState<number | null>(null);
    const [itemData, setItemData] = useState<any>(null);
    const [outsourcedCompany, setOutsourcedCompany] = useState('');
    const [contact, setContact] = useState('');
    const [expectedDate, setExpectedDate] = useState('');
    const [comment, setComment] = useState('');
    const [saving, setSaving] = useState(false);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const params = new URLSearchParams(props.history.location.search);
        const tiId = params.get('ti');
        const wrId = params.get('wr');

        if (tiId) {
            setSource('transport');
            setItemId(parseInt(tiId));
            props.actions.fetchWithoutInstallationItem(parseInt(tiId), 'transport').then((data: any) => {
                if (data) setItemData(data);
                setLoading(false);
            });
        } else if (wrId) {
            setSource('warehouse');
            setItemId(parseInt(wrId));
            props.actions.fetchWithoutInstallationItem(parseInt(wrId), 'warehouse').then((data: any) => {
                if (data) setItemData(data);
                setLoading(false);
            });
        } else {
            setLoading(false);
        }
    }, []);

    const handleSubmit = async () => {
        if (!outsourcedCompany.trim()) {
            toast.error(fm('installation.without.toast.companyRequired', 'Kompania e instalimit është e detyrueshme'));
            return;
        }

        setSaving(true);
        try {
            const payload: any = {
                outsourced_company: outsourcedCompany.trim(),
                contact: contact.trim() || undefined,
                expected_date: expectedDate || undefined,
                comment: comment.trim() || undefined,
            };

            if (source === 'transport' && itemId) {
                payload.transport_item_id = itemId;
            } else if (source === 'warehouse' && itemId) {
                payload.warehouse_record_id = itemId;
            }

            const result = await props.actions.createWithoutInstallation(payload);
            if (result?.error) {
                toast.error(result.message || fm('installation.without.toast.somethingWrong', 'Diçka shkoi gabim'));
            } else {
                toast.success(fm('installation.without.toast.success', 'Instalimi u regjistrua'));
                props.history.push('/dashboard/installation');
            }
        } catch (err) {
            toast.error(fm('installation.without.toast.somethingWrong', 'Diçka shkoi gabim'));
        }
        setSaving(false);
    };

    if (loading) {
        return (
            <div className="clp-page" style={{ padding: 20 }}>
                <div style={{ textAlign: 'center', padding: 40, color: '#94a3b8' }}>{fm('installation.without.loading', 'Duke ngarkuar...')}</div>
            </div>
        );
    }

    return (
        <div className="clp-page" style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            <MikaPageHeader
                title={fm('installation.without.title', 'Pa instalim — Konfirmo')}
                onBack={() => props.history.goBack()}
                rightContent={
                    <button
                        className="inst-action-finish"
                        style={{ border: 'none', cursor: saving ? 'not-allowed' : 'pointer', opacity: saving ? 0.6 : 1 }}
                        onClick={handleSubmit}
                        disabled={saving}
                    >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="20 6 9 17 4 12"/></svg>
                        <FormattedMessage id="installation.without.confirm" defaultMessage="Konfirmo" />
                    </button>
                }
            />

            <div style={{ flex: 1, overflow: 'auto', padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: 16 }}>
                {/* Item info card */}
                {itemData && (
                    <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, overflow: 'hidden' }}>
                        <div className="t-card-header">
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/></svg>
                            <FormattedMessage id="installation.without.detailsHeader" defaultMessage="Detajet" />
                            <span className="t-card-header-badge">{itemData.warehouseLabel}</span>
                        </div>
                        <div className="t-card-body">
                            <div className="t-delivery-info">
                                <div className="t-delivery-field">
                                    <div className="t-delivery-label"><FormattedMessage id="production.detail.client" defaultMessage="Klienti" /></div>
                                    <div className="t-delivery-value">{itemData.client?.name || '—'}</div>
                                </div>
                                <div className="t-delivery-field">
                                    <div className="t-delivery-label"><FormattedMessage id="production.detail.project" defaultMessage="Projekti" /></div>
                                    <div className="t-delivery-value">{itemData.project?.name || '—'}</div>
                                </div>
                                <div className="t-delivery-field">
                                    <div className="t-delivery-label"><FormattedMessage id="installation.col.city" defaultMessage="Qyteti" /></div>
                                    <div className="t-delivery-value">{itemData.client?.city || '—'}</div>
                                </div>
                            </div>
                            {itemData.products && itemData.products.length > 0 && (
                                <div className="t-products-section">
                                    <div className="t-wr-header">
                                        <span className="t-wr-label"><FormattedMessage id="installation.without.productsHeader" defaultMessage="Produkte" /></span>
                                        <span className="t-wr-badge">{itemData.products.length}</span>
                                    </div>
                                    {itemData.products.map((p: any, i: number) => (
                                        <div key={i} className="t-product-row">
                                            <span className="t-product-name">{p.productName || '—'}</span>
                                            {p.type && <span className="t-product-type">{p.type}</span>}
                                            <span className="t-product-qty">{p.quantity || ''} {p.unit || ''}</span>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {/* Form fields */}
                <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, overflow: 'hidden' }}>
                    <div className="t-card-header">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                        <FormattedMessage id="installation.without.registrationHeader" defaultMessage="Regjistrimi" />
                    </div>
                    <div className="t-card-body" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                        <div>
                            <label style={{ fontSize: 11, color: '#64748b', fontWeight: 600, display: 'block', marginBottom: 4 }}>
                                <FormattedMessage id="installation.without.companyLabel" defaultMessage="Kompania e instalimit" /> <span style={{ color: '#ef4444' }}>*</span>
                            </label>
                            <input
                                style={inputStyle}
                                placeholder={fm('installation.without.companyPlaceholder', 'Kush e ka bërë instalimin?')}
                                value={outsourcedCompany}
                                onChange={e => setOutsourcedCompany(e.target.value)}
                            />
                        </div>
                        <div>
                            <label style={{ fontSize: 11, color: '#64748b', fontWeight: 600, display: 'block', marginBottom: 4 }}>
                                <FormattedMessage id="installation.without.contactLabel" defaultMessage="Kontakti" />
                            </label>
                            <input
                                style={inputStyle}
                                placeholder={fm('installation.without.contactPlaceholder', 'Nr. telefoni ose email (opsionale)')}
                                value={contact}
                                onChange={e => setContact(e.target.value)}
                            />
                        </div>
                        <div>
                            <label style={{ fontSize: 11, color: '#64748b', fontWeight: 600, display: 'block', marginBottom: 4 }}>
                                <FormattedMessage id="installation.without.expectedDateLabel" defaultMessage="Data e pritur" />
                            </label>
                            <input
                                type="date"
                                style={inputStyle}
                                value={expectedDate}
                                onChange={e => setExpectedDate(e.target.value)}
                            />
                        </div>
                        <div>
                            <label style={{ fontSize: 11, color: '#64748b', fontWeight: 600, display: 'block', marginBottom: 4 }}>
                                <FormattedMessage id="installation.without.commentLabel" defaultMessage="Komenti" />
                            </label>
                            <textarea
                                style={{ ...inputStyle, minHeight: 80, resize: 'vertical' } as React.CSSProperties}
                                placeholder={fm('installation.without.commentPlaceholder', 'Shënim shtesë (opsionale)')}
                                value={comment}
                                onChange={e => setComment(e.target.value)}
                            />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

const mapStateToProps = (state: any) => ({
    loading: state.app.loading,
});

const mapDispatchToProps = (dispatch: any) => ({
    actions: bindActionCreators(installationActions as any, dispatch),
});

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(InstallationWithoutForm) as any) as any;
